// src/pages/accounting/HorseLedgerInsidePage.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Stack,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import axios from 'axios';

const apiUrl = process.env.REACT_APP_API_URL;

/* ----------------------------- Helpers ----------------------------- */

function toISODate(d) {
  if (!d) return '';
  const x = new Date(d);
  const yyyy = x.getFullYear();
  const mm = String(x.getMonth() + 1).padStart(2, '0');
  const dd = String(x.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function getDefaultMonthRange() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  return { from: toISODate(from), to: toISODate(now) };
}

function safeNum(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}

async function fetchInsideHorses() {
  const res = await axios.get(`${apiUrl}/horses/inside`);
  return res.data;
}

async function fetchHorseLedgerInside({ horse_id, from, to }) {
  const res = await axios.get(
    `${apiUrl}/accounting/finance/horse-ledger-inside`,
    {
      params: { horse_id, from, to },
    }
  );
  return res.data;
}

/* ----------------------------- Component ----------------------------- */

export default function HorseLedgerInsidePage() {
  const { t, i18n } = useTranslation();

  const dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
  const { from: defaultFrom, to: defaultTo } = useMemo(
    getDefaultMonthRange,
    []
  );

  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);

  const [horses, setHorses] = useState([]);
  const [horseLoading, setHorseLoading] = useState(false);
  const [horse, setHorse] = useState(null);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const totalAmount = useMemo(
    () => rows.reduce((a, r) => a + safeNum(r.amount), 0),
    [rows]
  );

  useEffect(() => {
    let mounted = true;

    (async () => {
      setHorseLoading(true);
      try {
        const list = await fetchInsideHorses();
        if (!mounted) return;
        setHorses(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!mounted) return;
        setHorses([]);
      } finally {
        if (mounted) setHorseLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const canSearch = !!horse?.horse_id && !!from && !!to && apiUrl;

  const handleSearch = async () => {
    if (!canSearch) return;
    setLoading(true);
    try {
      const data = await fetchHorseLedgerInside({
        horse_id: horse.horse_id,
        from,
        to,
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!rows.length) return;

    const exportRows = rows.map((r, idx) => ({
      '#': idx + 1,
      Date: r.txn_date || '',
      Service: r.service_type || '',
      Description: r.description || '',
      Amount: safeNum(r.amount),
      Invoice: r.invoice_number || '',
      ReviewStatus: r.review_status || '',
      Notes: r.notes || '',
    }));

    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'HorseLedgerInside');

    const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    saveAs(
      new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
      `horse_ledger_inside_${horse?.horse_id || 'x'}_${from}_to_${to}.xlsx`
    );
  };

  return (
    <Box dir={dir} sx={{ p: 2 }}>
      <Paper sx={{ p: 2, borderRadius: 2 }}>
        {/* Header */}
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {t('horse_ledger_inside', 'Horse Ledger (Inside)')}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {t('filter_and_export', 'Filter, view, and export transactions')}
            </Typography>
            {!apiUrl ? (
              <Typography variant="body2" sx={{ mt: 0.5, color: 'error.main' }}>
                {t('api_url_missing', 'Missing REACT_APP_API_URL in .env')}
              </Typography>
            ) : null}
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={!canSearch || loading}
            >
              {loading ? t('loading', 'Loading...') : t('search', 'Search')}
            </Button>
            <Button
              variant="outlined"
              onClick={handleExport}
              disabled={!rows.length}
            >
              {t('export_excel', 'Export Excel')}
            </Button>
          </Stack>
        </Box>

        {/* Filters */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={horses}
              loading={horseLoading}
              value={horse}
              onChange={(e, v) => setHorse(v)}
              isOptionEqualToValue={(a, b) => a?.horse_id === b?.horse_id}
              getOptionLabel={(opt) => {
                if (!opt) return '';

                const horseAr = opt.name_ar || opt.horse_name_ar || opt.nameAr;
                const horseEn = opt.name || opt.horse_name || opt.name_en;

                const ownerAr =
                  opt.owner_name_ar || opt.ownerAr || opt.owner_nameAr;
                const ownerEn =
                  opt.owner_name || opt.ownerName || opt.owner_name_en;

                const horseLabel =
                  horseAr && horseEn
                    ? `${horseAr} - ${horseEn}`
                    : horseAr || horseEn || '';
                const ownerLabel =
                  ownerAr && ownerEn
                    ? `${ownerAr} - ${ownerEn}`
                    : ownerAr || ownerEn || '';

                if (!horseLabel && !ownerLabel)
                  return String(opt.horse_id || '');
                if (!ownerLabel) return horseLabel;

                return `${horseLabel} | ${ownerLabel}`;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('select_horse', 'Select Horse')}
                  placeholder={t('select_horse_hint', 'Choose a horse')}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {horseLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label={t('from_date', 'From')}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label={t('to_date', 'To')}
              value={to}
              onChange={(e) => setTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        {/* Summary */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 1.5, borderRadius: 2 }}>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {t('transactions_count', 'Transactions')}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {rows.length}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 1.5, borderRadius: 2 }}>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {t('total_amount', 'Total Amount')}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {totalAmount.toLocaleString()}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Table */}
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={60}>#</TableCell>
                <TableCell>{t('date', 'Date')}</TableCell>
                <TableCell>{t('service_type', 'Service')}</TableCell>
                <TableCell>{t('description', 'Description')}</TableCell>
                <TableCell align="right">{t('amount', 'Amount')}</TableCell>
                <TableCell>{t('invoice_number', 'Invoice')}</TableCell>
                <TableCell>{t('review_status', 'Review')}</TableCell>
                <TableCell>{t('notes', 'Notes')}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box
                      sx={{ py: 4, display: 'flex', justifyContent: 'center' }}
                    >
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : rows.length ? (
                rows.map((r, idx) => (
                  <TableRow key={`${r.txn_id || idx}-${idx}`} hover>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{r.txn_date || ''}</TableCell>
                    <TableCell>{r.service_type || ''}</TableCell>
                    <TableCell>{r.description || ''}</TableCell>
                    <TableCell align="right">
                      {safeNum(r.amount).toLocaleString()}
                    </TableCell>
                    <TableCell>{r.invoice_number || ''}</TableCell>
                    <TableCell>{r.review_status || ''}</TableCell>
                    <TableCell>{r.notes || ''}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box sx={{ py: 4, textAlign: 'center', opacity: 0.8 }}>
                      {t('no_data', 'No data')}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
